/** biome-ignore-all lint/performance/noNamespaceImport: Good for primitives */
import * as Select from '@radix-ui/react-select';
import { tv } from 'tailwind-variants';
import { Icon } from '@/app_components/common/Icon';
import type { IconName } from '@/app_components/icons/constants';
import { cn } from '@/app_lib/utils/utils';

interface SelectV2Props {
  label: string;
  id: string;
  value?: string;
  onChange: (id: string, value: string) => void;
  options: { label: string; value: string }[];
  placeholder?: string;
  error?: string;
  required?: boolean;
  className?: string;
  icon?: IconName;
}

const triggerVariants = tv({
  base: 'flex justify-between border rounded-sm stroke-helper-2 active:stroke-accent bg-pure',
  defaultVariants: {
    size: 'md',
  },
  variants: {
    size: {
      lg: 'typography-default input-p-lg',
      md: 'typography-sm input-p-md',
      sm: 'typography-xs input-p-sm',
    },
    variant: {
      error: 'stroke-red',
    },
  },
});

const contentVariants = tv({
  base: 'flex justify-between border rounded-sm stroke-helper-2 active:stroke-accent bg-white',
  defaultVariants: {
    size: 'md',
  },
  variants: {
    size: {
      lg: 'typography-default input-p-lg max-h-51.5',
      md: 'typography-sm max-h-49',
      sm: 'typography-xs input-p-sm max-h-45.25',
    },
  },
});

const labelVariants = tv({
  base: 'flex gap-xs typography-sm',
  variants: {
    variant: {
      error: 'text-red',
    },
  },
});

export const SelectV2 = ({
  className,
  error,
  icon,
  id,
  label,
  onChange,
  options,
  required,
  placeholder = 'Select...',
  value,
}: SelectV2Props) => {
  return (
    <div className={cn('flex flex-col gap-xs', className)}>
      <label
        className={cn(labelVariants({ variant: error ? 'error' : undefined }), '')}
        htmlFor={id}
      >
        {label}
        {required && (
          <span
            aria-hidden='true'
            className='icon-red'
          >
            *
          </span>
        )}
      </label>
      <Select.Root
        onValueChange={(val) => {
          const updatedVal = val === 'clear' ? '' : val;
          onChange(id, updatedVal);
        }}
        value={value}
      >
        <Select.Trigger
          className={cn(
            triggerVariants({ variant: error ? 'error' : undefined }),
            `w-full items-center ${value ? '' : 'text-secondary'}`,
          )}
        >
          <Select.Value placeholder={placeholder} />
          <Select.Icon>
            <Icon
              icon={icon || 'ButtonDown'}
              size='sm'
            />
          </Select.Icon>
        </Select.Trigger>
        <Select.Content
          align='center'
          className={cn(contentVariants(), 'overflow-y-auto py-xxs z-body-popover isolate')}
          position='popper'
          sideOffset={4}
        >
          <Select.Viewport className='min-w-[var(--radix-select-trigger-width)]'>
            <Select.Item
              className='hover:bg-secondary cursor-pointer py-xxs px-xs text-secondary'
              value='clear'
            >
              <Select.ItemText>{placeholder}</Select.ItemText>
            </Select.Item>
            {options.map((opt) => (
              <Select.Item
                className='hover:bg-secondary cursor-pointer py-xxs px-xs'
                key={opt.value}
                value={opt.value}
              >
                <Select.ItemText>{opt.label}</Select.ItemText>
              </Select.Item>
            ))}
          </Select.Viewport>
        </Select.Content>
      </Select.Root>
      {error && (
        <p
          className='text-red typography-xs'
          id={`${id}-error`}
        >
          {error}
        </p>
      )}
    </div>
  );
};
